import { Calendar as CalendarIcon, RotateCcw } from "lucide-react";
import { format } from "date-fns";
import { TimelineFilters } from "@/types/application";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { DateRange } from "react-day-picker";

interface VersionTimelineFiltersProps {
  filters: TimelineFilters;
  onFiltersChange: (filters: TimelineFilters) => void;
}

export const VersionTimelineFilters = ({ filters, onFiltersChange }: VersionTimelineFiltersProps) => {
  const dateRange: DateRange | undefined = {
    from: filters.dateRange.start || undefined,
    to: filters.dateRange.end || undefined
  };

  return (
    <Card className="border-border">
      <CardHeader>
        <CardTitle className="text-sm font-medium">Filters</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Date Range Picker */}
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Date Range</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start text-left text-xs h-9">
                <CalendarIcon className="mr-2 h-3 w-3" />
                {filters.dateRange.start ? (
                  filters.dateRange.end ? (
                    <>
                      {format(filters.dateRange.start, "LLL dd, y")} -{" "}
                      {format(filters.dateRange.end, "LLL dd, y")}
                    </>
                  ) : (
                    format(filters.dateRange.start, "LLL dd, y")
                  )
                ) : (
                  <span>Pick a date range</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="range"
                selected={dateRange}
                onSelect={(range) => {
                  onFiltersChange({
                    ...filters,
                    dateRange: {
                      start: range?.from || null,
                      end: range?.to || null
                    }
                  });
                }}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Change Type Checkboxes */}
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Change Types</Label>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Checkbox
                id="keyChanges"
                checked={filters.changeTypes.keyChanges}
                onCheckedChange={(checked) => {
                  onFiltersChange({
                    ...filters,
                    changeTypes: {
                      ...filters.changeTypes,
                      keyChanges: checked as boolean
                    }
                  });
                }}
              />
              <Label htmlFor="keyChanges" className="text-xs font-normal cursor-pointer">
                Key Changes
              </Label>
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                id="customerChanges"
                checked={filters.changeTypes.customerChanges}
                onCheckedChange={(checked) => {
                  onFiltersChange({
                    ...filters,
                    changeTypes: {
                      ...filters.changeTypes,
                      customerChanges: checked as boolean
                    }
                  });
                }}
              />
              <Label htmlFor="customerChanges" className="text-xs font-normal cursor-pointer">
                Customer Changes
              </Label>
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                id="activationChanges"
                checked={filters.changeTypes.activationChanges}
                onCheckedChange={(checked) => {
                  onFiltersChange({
                    ...filters,
                    changeTypes: {
                      ...filters.changeTypes,
                      activationChanges: checked as boolean
                    }
                  });
                }}
              />
              <Label htmlFor="activationChanges" className="text-xs font-normal cursor-pointer">
                Activation Changes
              </Label>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="nameChanges"
                checked={filters.changeTypes.nameChanges}
                onCheckedChange={(checked) => {
                  onFiltersChange({
                    ...filters,
                    changeTypes: {
                      ...filters.changeTypes,
                      nameChanges: checked as boolean
                    }
                  });
                }}
              />
              <Label htmlFor="nameChanges" className="text-xs font-normal cursor-pointer">
                Name/Description Changes
              </Label>
            </div>
          </div>
        </div>

        {/* Milestones Only Toggle */}
        <div className="flex items-center justify-between gap-2">
          <Label htmlFor="milestonesOnly" className="text-xs font-normal cursor-pointer">
            Show Milestones Only
          </Label>
          <Switch
            id="milestonesOnly"
            checked={filters.showMilestonesOnly}
            onCheckedChange={(checked) => {
              onFiltersChange({
                ...filters,
                showMilestonesOnly: checked
              });
            }}
          />
        </div>

        {/* Reset Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            onFiltersChange({
              dateRange: { start: null, end: null },
              changeTypes: {
                keyChanges: true,
                customerChanges: true,
                activationChanges: true,
                nameChanges: true
              },
              showMilestonesOnly: false
            });
          }}
          className="w-full h-8 text-xs"
        >
          <RotateCcw className="h-3 w-3 mr-2" />
          Reset Filters
        </Button>
      </CardContent>
    </Card>
  );
};
